import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  isContestProblem, 
  getContestProblemName, 
  getContestProblemDescription,
  getContestProblemTags,
  getContestYear,
  getContestInputTargets
} from '../utils/contestUtils';

interface ContestProblemDescriptionProps {
  problemId: string;
  readme: string;
}

export default function ContestProblemDescription({ 
  problemId, 
  readme 
}: ContestProblemDescriptionProps) {
  const bgColor = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('blue.200', 'blue.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');

  if (!isContestProblem(problemId)) {
    return null;
  }

  const contestName = getContestProblemName(problemId);
  const contestDescription = getContestProblemDescription(problemId);
  const contestTags = getContestProblemTags(problemId);
  const contestYear = getContestYear(problemId);
  const inputTargets = getContestInputTargets(problemId);

  return (
    <Box 
      id="problem-description"
      bg={bgColor} 
      border="1px" 
      borderColor={borderColor} 
      borderRadius="lg" 
      p={6}
      mb={4}
    >
      {/* Contest Header */}
      <VStack spacing={4} align="stretch">
        {/* Contest Title */}
        <Box>
          <HStack spacing={3} mb={2}>
            <Badge colorScheme="blue" variant="solid" size="lg">
              SPE4IC {contestYear}
            </Badge>
            <Badge colorScheme="green" variant="solid">
              Active Contest
            </Badge>
          </HStack>
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            {contestName}
          </Text>
          <Text fontSize="md" color={textColor} opacity={0.8} mt={1}>
            {contestDescription}
          </Text>
        </Box>

        <Divider />

        {/* Contest Tags */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2} color={textColor}>
            Problem Tags:
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            {contestTags.map((tag) => (
              <Badge
                key={tag}
                colorScheme="blue"
                variant="outline"
                size="sm"
              >
                {tag}
              </Badge>
            ))}
          </HStack>
        </Box>

        <Divider />

        {/* Contest Alert */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Contest Problem</AlertTitle>
            <AlertDescription>
              This is an active contest problem. Your submissions will be evaluated on both correctness and performance across multiple graph datasets.
            </AlertDescription>
          </Box>
        </Alert>

        <Divider />

        {/* Input Targets */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2} color={textColor}>
            Test Datasets:
          </Text>
          <Text fontSize="sm" color={textColor} opacity={0.8} mb={2}>
            Your solution will be tested on the following graph datasets:
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            {inputTargets.map((target) => (
              <Badge
                key={target}
                colorScheme="gray"
                variant="subtle"
                size="sm"
              >
                {target}
              </Badge>
            ))}
          </HStack>
        </Box>

        <Divider />

        {/* Problem Description */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={3} color={textColor}>
            Problem Description:
          </Text>
          <Box
            bg="white"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            p={4}
            maxH="400px"
            overflowY="auto"
            fontSize="sm"
            lineHeight="1.6"
            color="gray.800"
            sx={{
              '& h1': { fontSize: '1.5em', fontWeight: 'bold', mb: 2 },
              '& h2': { fontSize: '1.3em', fontWeight: 'bold', mb: 2, mt: 4 },
              '& h3': { fontSize: '1.1em', fontWeight: 'bold', mb: 1, mt: 3 },
              '& p': { mb: 2 },
              '& ul': { pl: 4, mb: 2 },
              '& li': { mb: 1 },
              '& code': { bg: 'gray.100', px: 1, py: 0.5, borderRadius: 'sm' },
              '& pre': { bg: 'gray.100', p: 2, borderRadius: 'sm', overflowX: 'auto' }
            }}
            dangerouslySetInnerHTML={{ __html: readme }}
          />
        </Box>
      </VStack>
    </Box>
  );
} 